import React from 'react';
import { observer } from 'mobx-react';

import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';

import { Button } from 'components/Button';

import DataError from 'containers/Error/DataError';

import EventDetailsStore from 'stores/EventDetails';

import Row from 'components/Row';

import { AnswerTypes } from 'utils/api/modules/incidents/incidents.types';

import CustomFieldDisplayValue from 'components/SharedFormComponents/CustomFieldDisplayValue';
import { ProtectorType } from 'core/constants';

import OriginalIncident from 'components/SharedFormComponents/OriginalIncident/OriginalIncident';

import WrappedDataError from 'containers/Error/WrappedDataError/WrappedDataError';

import { EntitiesStores } from '../types';
import OriginalSubmissionHeader from './OriginalSubmissionHeader';

import { SectionContainer, RowContainer, ButtonContainer } from './styles';
import ReporterComments from './ReporterComments';
import ReporterDetailsSection from './ReporterDetailsSection';
import { AmendmentNotice, EarlierVersionBar, VersionHistoryDrawer, useVersionHistory } from './SubmissionHistory';
import SubmissionSection from './SubmissionSection';

const ToggleEmptyAnswersButton: React.FC<{
  hideEmptyAnswers?: boolean;
  setHideEmptyAnswers: (hide: boolean) => void;
}> = ({ hideEmptyAnswers, setHideEmptyAnswers }) => (
  <Button variant="outlined" onClick={() => setHideEmptyAnswers(!hideEmptyAnswers)} sx={{ gap: '8px' }}>
    {hideEmptyAnswers ? (
      <>
        <VisibilityOffOutlinedIcon />
        Show all answers
      </>
    ) : (
      <>
        <VisibilityOutlinedIcon />
        Hide empty answers
      </>
    )}
  </Button>
);

const EntitySubmissionTab: React.FunctionComponent<{
  store: EntitiesStores;
  hideEmptyAnswers?: boolean;
  setHideEmptyAnswers?: (hideEmptyAnswers: boolean) => void;
}> = ({ store, hideEmptyAnswers, setHideEmptyAnswers }): React.ReactElement => {
  const { details, prefix } = store;
  const detailsType = details as EventDetailsStore['details'];

  const versionStore = store instanceof EventDetailsStore ? store : null;

  const versionHistory = useVersionHistory(
    versionStore,
    detailsType?.uuid,
    detailsType?.amendmentSummary ?? null,
    versionStore?.submissionVersions,
    versionStore?.loadingSubmissionVersions ?? false,
    versionStore?.submissionVersionsError ?? false
  );

  if (!details || !detailsType) {
    return <DataError />;
  }

  const eventId = detailsType.entity[`${prefix}id`];

  const { summary, displayed } = versionHistory;

  const absent = displayed ? new Set(displayed.absentKeys) : undefined;

  const displayedContent = (displayed ? displayed.content : detailsType.submission?.content) as Record<
    string,
    AnswerTypes
  >;

  if (detailsType.legacySubmission) {
    return (
      <>
        <ButtonContainer>
          {setHideEmptyAnswers && (
            <ToggleEmptyAnswersButton hideEmptyAnswers={hideEmptyAnswers} setHideEmptyAnswers={setHideEmptyAnswers} />
          )}
        </ButtonContainer>
        <OriginalIncident
          customHeader={
            <OriginalSubmissionHeader
              id={eventId.value as number}
              created={detailsType.createdAt}
              createdBy={detailsType.createdBy}
            />
          }
          hideEmptyAnswers={hideEmptyAnswers}
          details={{
            originalIncident: detailsType.legacySubmission,
          }}
        />

        <ReporterComments />
      </>
    );
  }

  if (!detailsType.submission && !detailsType.legacySubmission) {
    return <WrappedDataError />;
  }

  const { stack } = detailsType.submission;

  return (
    <>
      <SectionContainer>
        {summary && !displayed && <AmendmentNotice summary={summary} onOpen={versionHistory.onOpen} />}
        {displayed && (
          <EarlierVersionBar
            entry={displayed}
            onOpen={versionHistory.onOpen}
            onReturnToLatest={versionHistory.onReturnToLatest}
          />
        )}

        {setHideEmptyAnswers && (
          <ToggleEmptyAnswersButton hideEmptyAnswers={hideEmptyAnswers} setHideEmptyAnswers={setHideEmptyAnswers} />
        )}

        <OriginalSubmissionHeader
          id={eventId.value as number}
          created={detailsType.createdAt}
          createdBy={detailsType.createdBy}
        />

        <RowContainer key="form-name">
          <Row name="Form name">
            <CustomFieldDisplayValue type={ProtectorType.ShortText} value={detailsType.submission.form.name || 'N/A'} />
          </Row>
        </RowContainer>

        {detailsType.submission.publicSubmissionDetails &&
          Object.keys(detailsType.submission.publicSubmissionDetails).length > 0 && (
            <ReporterDetailsSection publicSubmissionDetails={detailsType.submission.publicSubmissionDetails} />
          )}

        {stack.map((sectionKey) => (
          <SubmissionSection
            key={sectionKey}
            form={detailsType.submission.form.form}
            sectionKey={sectionKey}
            content={displayedContent}
            absent={absent}
            hideEmptyAnswers={hideEmptyAnswers}
            imported={detailsType.submission.imported}
          />
        ))}
      </SectionContainer>
      <ReporterComments />
      {summary && <VersionHistoryDrawer state={versionHistory} />}
    </>
  );
};

export default observer(EntitySubmissionTab);
